/* eslint-disable react/prop-types */
import React from "react";
import { Tabs, Tag, InputNumber } from "antd";

export default function WaterTariffTable({ waterTariff = [], mode, setWaterTariff }) {
  // mode === true means read-only, mode === false means editing

  const updateField = (id, field, value) => {
    if (value < 0) return;
    const newData = waterTariff.map((item) => {
      if (item._id === id) {
        return { ...item, [field]: value };
      }
      return item;
    });
    setWaterTariff(newData);
  };

  const updateTierRate = (id, tierIndex, value) => {
    if (value < 0) return;
    const newData = waterTariff.map((item) => {
      if (item._id === id) {
        const copy = JSON.parse(JSON.stringify(item));
        if (copy.tiers && copy.tiers[tierIndex]) {
          copy.tiers[tierIndex].rate = value;
        }
        return copy;
      }
      return item;
    });
    setWaterTariff(newData);
  };

  const renderValue = (val, prefix = "RM ", suffix = "/m³") => (
    <span className="fw-semibold text-dark">
      {prefix}
      {Number(val || 0).toFixed(2)}
      {suffix ? <small className="text-muted ms-1">{suffix}</small> : null}
    </span>
  );

  const renderNumberInput = (value, onChange, step = 0.01, width = 85) => {
    if (mode) {
      return renderValue(value);
    }
    return (
      <InputNumber
        size="small"
        min={0}
        step={step}
        value={value}
        onChange={onChange}
        style={{ width, textAlign: "right" }}
      />
    );
  };

  const uniqueWater = Array.from(
    new Map((waterTariff || []).map((item) => [item.name, item])).values()
  );
  const domesticTariffs = uniqueWater.filter((t) => t.categoryGroup === "domestic");
  const nonDomesticTariffs = uniqueWater.filter((t) => t.categoryGroup === "nonDomestic");

  const renderTariffGroup = (items) => {
    return (
      <div className="card shadow-sm border-0 p-3 bg-white rounded-3">
        <div className="table-responsive">
          <table className="table table-bordered table-hover align-middle mb-0">
            <thead style={{ background: "#F1F5F9" }}>
              <tr>
                <th style={{ width: "35%" }}>Category</th>
                <th style={{ width: "25%" }}>Consumption Band / Type</th>
                <th style={{ width: "20%", textAlign: "right" }}>Rate (RM/m³)</th>
                <th style={{ width: "20%", textAlign: "right" }}>Minimum Charge (RM)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                if (item.type === "tiered" && item.tiers?.length) {
                  return item.tiers.map((tier, idx) => (
                    <tr key={`${item._id || item.name}-${idx}`}>
                      {idx === 0 && (
                        <td rowSpan={item.tiers.length} className="fw-bold align-middle" style={{ color: "#184C78" }}>
                          {item.name}
                          <Tag color="blue" className="ms-2">
                            Tiered
                          </Tag>
                        </td>
                      )}
                      <td>
                        {tier.end > 0
                          ? `${tier.start} m³ until ${tier.end} m³`
                          : `> ${tier.start} m³ onwards`}
                      </td>
                      <td className="text-end">
                        {renderNumberInput(tier.rate, (val) => updateTierRate(item._id, idx, val))}
                      </td>
                      {idx === 0 && (
                        <td rowSpan={item.tiers.length} className="text-end align-middle">
                          {renderNumberInput(
                            item.minCharge,
                            (val) => updateField(item._id, "minCharge", val),
                            1,
                            90
                          )}
                        </td>
                      )}
                    </tr>
                  ));
                }

                // Flat rate category
                return (
                  <tr key={item._id || item.name}>
                    <td className="fw-bold" style={{ color: "#184C78" }}>
                      {item.name}
                      <Tag color="cyan" className="ms-2">
                        Flat Rate
                      </Tag>
                    </td>
                    <td>Flat Rate per m³</td>
                    <td className="text-end">
                      {renderNumberInput(item.rate, (val) => updateField(item._id, "rate", val))}
                    </td>
                    <td className="text-end">
                      {renderNumberInput(item.minCharge, (val) => updateField(item._id, "minCharge", val), 1, 90)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const tabItems = [
    {
      key: "domestic",
      label: "Domestic Category",
      children: renderTariffGroup(domesticTariffs),
    },
    {
      key: "nonDomestic",
      label: "Non-Domestic & Special Categories",
      children: renderTariffGroup(nonDomesticTariffs),
    },
  ];

  return (
    <div className="water-tariff-table">
      <Tabs defaultActiveKey="domestic" items={tabItems} className="custom-water-tabs" />
    </div>
  );
}
